import { useAccount } from "@qw/auth";
import { CHAT_FILE_ACCEPT, CHAT_FILE_MAX_SIZE } from "@qw/chat";
import {
  File_Audience,
  File_Source,
  type MarketTypes,
  type ProductQuery,
  type UpdateProductInput,
  useUpdateProductMutation,
} from "@qw/graphql";
import {
  AutosaveIndicator,
  AutosaveWrapper,
  Button,
  FieldFileUpload,
  FieldInput,
  Form,
  Group,
  Icon,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  UnsavedChangesDialog,
  uploadFiles,
  useAutosaveHandler,
  useNavigationBlocking,
} from "@qw/ui";
import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { usePage } from "@/providers/use-page";
import { AuthoringEditorPanes } from "../../components/authoring-editor-panes";
import { CarrierContactsSection } from "../carrier-contacts/carrier-contacts-section";
import { HistoricalInsightsPanel } from "../historical-insights/historical-insights-panel";
import { EnabledSwitch } from "./enabled-switch";
import { GrossCommissionField } from "./gross-commission-field";
import { ProductEditorChatPanel } from "./product-editor-chat-panel";
import { ProductFormSchema, type ProductFormValues } from "./product-form.types";
import { ProductMetadataFields } from "./product-metadata-fields";
import { ProductWorkflowEditor, type ProductWorkflowEditorHandle } from "./product-workflow-editor";

export interface ProductFormProps {
  product: ProductQuery["product"];
  canEdit: boolean;
  canManageContacts: boolean;
  refetchProduct: () => Promise<unknown>;
}

const getProductInput = (values: ProductFormValues): UpdateProductInput => ({
  name: values.name,
  workflow: values.workflow,
  fileUploadIds: values.files.map((f) => f.id),
  carrierId: values.carrierId ?? null,
  lineOfBusinessIds: values.lineOfBusinessIds,
  enabled: values.enabled,
  marketType: values.marketType as MarketTypes | null | undefined,
  grossCommission: values.grossCommission,
});

const getProductInputSignature = (input: UpdateProductInput): string =>
  JSON.stringify({
    ...input,
    fileUploadIds: [...(input.fileUploadIds ?? [])].sort(),
    lineOfBusinessIds: [...(input.lineOfBusinessIds ?? [])].sort(),
  });

export const ProductForm: FC<ProductFormProps> = ({ canEdit, canManageContacts, product, refetchProduct }) => {
  const { user } = useAccount();
  const { setChatPanel, setIsChatOpen } = usePage();
  const navigate = useNavigate();
  const params = useParams();
  const [isUploading, setIsUploading] = useState(false);

  const [updateProduct] = useUpdateProductMutation();

  const productId = params.productId ?? null;

  const isExternalProduct = product.userId !== user.id;

  // Only remount on remote updates when the form is read-only.
  // For editable products, remounting on every updatedAt change causes autosave loops.
  const externalVersion = isExternalProduct && !canEdit ? (product?.updatedAt ?? "loading") : "";
  const editorKey = `${productId ?? "new"}:${isExternalProduct ? "external" : "owned"}:${externalVersion}`;

  const defaultValues: ProductFormValues = {
    name: product.name ?? "",
    workflow: product.workflow ?? "",
    files:
      product.files?.map((f) => ({
        id: f.fileUploadId,
        filename: f.filename,
        size: f.size,
        mimeType: f.mimeType,
      })) ?? [],
    carrierId: product.carrierId ?? undefined,
    lineOfBusinessIds: product.lineOfBusinessIds ?? [],
    enabled: product.enabled ?? false,
    marketType: product.marketType ?? undefined,
    grossCommission: product.grossCommission ?? null,
  };

  const lastSavedSignatureRef = useRef(getProductInputSignature(getProductInput(defaultValues)));
  const isUploadingWhenBlockedRef = useRef(false);
  const workflowEditorRef = useRef<ProductWorkflowEditorHandle>(null);
  const closedChatForProductIdRef = useRef<string | null>(null);
  const [workflowMarkdownState, setWorkflowMarkdownState] = useState<{ editorKey: string; markdown: string | null }>({
    editorKey,
    markdown: null,
  });
  const currentWorkflowMarkdown = workflowMarkdownState.editorKey === editorKey ? workflowMarkdownState.markdown : null;

  const insertMarkdownAtCursor = useCallback((markdown: string) => {
    workflowEditorRef.current?.insertContentAtCursor(markdown);
  }, []);

  const authoringChatPanel = useMemo(() => {
    if (!canEdit || !productId) {
      return null;
    }

    return (
      <ProductEditorChatPanel
        currentWorkflowMarkdown={currentWorkflowMarkdown ?? ""}
        isWorkflowMarkdownReady={currentWorkflowMarkdown !== null}
        onInsert={insertMarkdownAtCursor}
        productId={productId}
      />
    );
  }, [canEdit, currentWorkflowMarkdown, insertMarkdownAtCursor, productId]);

  useEffect(() => {
    setChatPanel(authoringChatPanel);
  }, [authoringChatPanel, setChatPanel]);

  useEffect(() => {
    return () => setChatPanel(null);
  }, [setChatPanel]);

  useLayoutEffect(() => {
    if (productId && closedChatForProductIdRef.current !== productId) {
      setIsChatOpen(false);
      closedChatForProductIdRef.current = productId;
    }
  }, [productId, setIsChatOpen]);

  const handleWorkflowMarkdownChange = useCallback(
    (markdown: string) => {
      setWorkflowMarkdownState({ editorKey, markdown });
    },
    [editorKey]
  );

  const handleUploadFiles = useCallback(async (files: File[]) => {
    const { fileData } = await uploadFiles(files, {
      audience: File_Audience.External,
      source: File_Source.ManualUpload,
    });
    return fileData;
  }, []);

  const handleSubmit = useCallback(
    async (values: ProductFormValues) => {
      if (!productId) {
        return;
      }

      const input = getProductInput(values);
      const inputSignature = getProductInputSignature(input);

      if (inputSignature === lastSavedSignatureRef.current) {
        return;
      }

      await updateProduct({
        variables: { id: productId, input },
        // Update cache to ensure the product list refreshes
        update: (cache, { data }) => {
          if (data?.updateProduct) {
            // Evict and refetch all products queries
            cache.evict({ fieldName: "enabledProducts" });
            cache.evict({ fieldName: "products" });
            cache.gc();
          }
        },
      });
      lastSavedSignatureRef.current = inputSignature;
      await refetchProduct();
    },
    [productId, updateProduct, refetchProduct]
  );

  const { isAutosavePaused, setIsAutosavePaused, saveStatus, handleAutosave, shouldWarnBeforeLeaving } =
    useAutosaveHandler({
      entityId: productId,
      canEdit,
      handleSubmit,
    });

  const blocker = useNavigationBlocking(shouldWarnBeforeLeaving || isUploading);

  useEffect(() => {
    if (blocker.state === "blocked" && isUploadingWhenBlockedRef.current === false) {
      isUploadingWhenBlockedRef.current = isUploading;
    } else if (blocker.state !== "blocked") {
      isUploadingWhenBlockedRef.current = false;
    }
  }, [blocker.state, isUploading]);

  return (
    <Form
      key={editorKey}
      mode="onChange"
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      validationSchema={ProductFormSchema}
      className="flex flex-auto min-h-0 min-w-0 w-full bg-card overflow-hidden"
    >
      <AuthoringEditorPanes
        autoSaveId="product-editor-panes"
        detailsLabel="product details"
        panelIds={{ editor: "product-editor", details: "product-details" }}
        details={({ collapseButton }) => (
          <div className="p-6">
            <Group type="flex" className="gap-6">
              {canEdit && productId ? (
                <>
                  <AutosaveIndicator
                    isAutosavePaused={isAutosavePaused}
                    setIsAutosavePaused={setIsAutosavePaused}
                    saveStatus={saveStatus}
                    trailingAction={collapseButton}
                    updatedAt={product.updatedAt}
                  />
                  <Separator orientation="horizontal" className="-mx-6 w-[calc(100%+3rem)]" />
                </>
              ) : (
                <div className="flex justify-end">{collapseButton}</div>
              )}
              <div className="flex">
                <EnabledSwitch canEdit={canEdit} />
              </div>

              <Separator orientation="horizontal" className="-mx-6 w-[calc(100%+3rem)]" />

              <ProductMetadataFields canEdit={canEdit} hasExistingCarrier={!!product.carrierId} />

              <Separator orientation="horizontal" className="-mx-6 w-[calc(100%+3rem)]" />

              <GrossCommissionField canEdit={canEdit} />

              <Separator orientation="horizontal" className="-mx-6 w-[calc(100%+3rem)]" />

              {productId && (
                <CarrierContactsSection
                  productId={productId}
                  submissionContacts={product.submissionCarrierContacts ?? []}
                  referenceContacts={product.referenceCarrierContacts ?? []}
                  canEdit={canManageContacts}
                />
              )}

              <Separator orientation="horizontal" className="-mx-6 w-[calc(100%+3rem)]" />

              <FieldFileUpload
                label="Files to reference in your workflow"
                name="files"
                placeholder="Add PDF, Excel, or Word files to reference in your workflow (max 25MB)."
                accept={CHAT_FILE_ACCEPT}
                maxSize={CHAT_FILE_MAX_SIZE}
                onUploadFiles={handleUploadFiles}
                onUploadingChange={setIsUploading}
                multiple
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </Group>
          </div>
        )}
      >
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden p-5 pl-4">
          <Tabs defaultValue="workflow" className="pr-10 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-4 gap-4">
              <div className="flex items-center gap-4">
                <Button type="button" variant="ghost" onClick={() => navigate("/products")} className="flex-none">
                  <Icon icon="arrow_back" />
                </Button>
                <FieldInput
                  name="name"
                  placeholder="Product name"
                  disabled={!canEdit}
                  readOnly={!canEdit}
                  inputProps={{
                    className:
                      "text-3xl font-bold border-0! bg-transparent px-0 ring-0! ring-offset-0! rounded-none! outline-none! shadow-none! disabled:opacity-100 disabled:bg-transparent",
                  }}
                />
              </div>

              <TabsList className="w-fit flex-0">
                <TabsTrigger value="workflow">Workflow</TabsTrigger>
                <TabsTrigger value="insights">Historical Insights</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="workflow" forceMount className="flex-1 min-h-0 data-[state=inactive]:hidden">
              <ProductWorkflowEditor
                ref={workflowEditorRef}
                editable={canEdit}
                content={defaultValues.workflow}
                product={product}
                isPaused={isAutosavePaused}
                onWorkflowMarkdownChange={handleWorkflowMarkdownChange}
              />
            </TabsContent>

            <TabsContent value="insights" className="flex-1 min-h-0 overflow-auto">
              <HistoricalInsightsPanel historicalInsights={product.historicalInsights} />
            </TabsContent>
          </Tabs>
        </div>
      </AuthoringEditorPanes>

      {productId && canEdit && (
        <AutosaveWrapper<ProductFormValues> onSave={handleAutosave} isPaused={isAutosavePaused || isUploading} />
      )}

      <UnsavedChangesDialog
        blocker={blocker}
        title={isUploadingWhenBlockedRef.current ? "Upload in Progress" : undefined}
        message={
          isUploadingWhenBlockedRef.current
            ? "Files are still uploading. Leaving now will cancel the upload."
            : undefined
        }
        confirmLabel={isUploadingWhenBlockedRef.current ? "Leave and cancel upload" : undefined}
      />
    </Form>
  );
};
